import React, { useEffect, useRef, useState } from "react";
import { getPost } from "../../api/api";
import Post from "../feed_page/Post";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Skeleton } from "../ui/skeleton";

/**
 * Dialog that loads a full feed post (including comments) by id.
 * @param {Object} props
 * @param {boolean} props.open
 * @param {function} props.onOpenChange
 * @param {number|null} props.postId
 * @param {number} props.userId
 * @param {object} props.user
 * @param {function} [props.onDeleted]
 * @param {number|null} [props.focusCommentId]
 * @param {function} [props.loadPost]
 * @returns {JSX.Element}
 */
function PostDetailModal({
  open,
  onOpenChange,
  postId,
  userId,
  user,
  onDeleted,
  focusCommentId = null,
  loadPost,
}) {
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const loadPostRef = useRef(loadPost);
  loadPostRef.current = loadPost;

  useEffect(() => {
    if (!open || !postId) {
      setPost(null);
      setError("");
      setLoading(false);
      return undefined;
    }

    let cancelled = false;

    async function load() {
      setLoading(true);
      setError("");
      setPost(null);
      try {
        const fetchPost = loadPostRef.current;
        const response = fetchPost
          ? await fetchPost(postId)
          : await getPost(postId, userId);
        if (cancelled) return;
        if (response?.success && response.data) {
          setPost(response.data);
        } else {
          setError(response?.error || response?.message || "Post not found");
        }
      } catch (err) {
        if (cancelled) return;
        console.error("Error loading post:", err);
        setError("Failed to load post");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [open, postId, userId]);

  const handleDeleted = () => {
    onDeleted?.(postId);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto border-0 bg-transparent p-0 shadow-none sm:rounded-xl">
        <DialogHeader className="sr-only">
          <DialogTitle>Post</DialogTitle>
          <DialogDescription>Post details and comments</DialogDescription>
        </DialogHeader>
        {loading ? (
          <div className="rounded-lg border border-ui-border bg-ui-card p-6">
            <div className="mb-4 flex items-center gap-3">
              <Skeleton className="h-12 w-12 rounded-full" />
              <div className="flex-1 space-y-2">
                <Skeleton className="h-4 w-1/3" />
                <Skeleton className="h-3 w-1/4" />
              </div>
            </div>
            <Skeleton className="mb-3 h-4 w-full" />
            <Skeleton className="h-40 w-full rounded-md" />
          </div>
        ) : null}

        {!loading && error ? (
          <p className="rounded-lg border border-ui-border bg-ui-card py-8 text-center text-sm text-error">
            {error}
          </p>
        ) : null}

        {!loading && !error && post ? (
          <div className="[&_.post]:mb-0">
            <Post
              userId={userId}
              user={user}
              post={post}
              hideContent={false}
              focusCommentId={focusCommentId}
              onDeleted={handleDeleted}
            />
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

export default PostDetailModal;
